/**
 * Pure SVG chart primitives (P3-F Doc 41) — no chart lib dep.
 *
 * 3 components: <BarChart>, <Sparkline>, <Donut>.
 * Tailwind classes cho color/responsive.
 */
import { cn } from "@/lib/utils";

interface BarChartProps {
  data: number[];
  labels?: string[];
  /** Optional second series cho stacked. */
  height?: number;
  formatValue?: (v: number) => string;
  className?: string;
}

/** Simple vertical bar chart. */
export function BarChart({
  data,
  labels,
  height = 160,
  formatValue,
  className,
}: BarChartProps): JSX.Element {
  const max = Math.max(1, ...data);
  const barW = 36;
  const gap = 8;
  const width = data.length * (barW + gap) - gap;
  const bottomPad = labels ? 28 : 8;

  return (
    <div className={cn("w-full overflow-x-auto", className)}>
      <svg
        width={width}
        height={height + bottomPad}
        className="text-foreground"
      >
        {data.map((v, i) => {
          const h = (v / max) * height;
          const x = i * (barW + gap);
          const y = height - h;
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={Math.max(2, h)}
                rx={3}
                className="fill-primary/80 hover:fill-primary transition-colors"
              >
                <title>
                  {labels?.[i] ?? `#${i + 1}`}: {formatValue ? formatValue(v) : v}
                </title>
              </rect>
              <text
                x={x + barW / 2}
                y={y - 4}
                textAnchor="middle"
                className="text-[10px] fill-muted-foreground"
              >
                {formatValue ? formatValue(v) : v}
              </text>
              {labels?.[i] ? (
                <text
                  x={x + barW / 2}
                  y={height + 16}
                  textAnchor="middle"
                  className="text-[10px] fill-muted-foreground"
                >
                  {labels[i]}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

interface LineChartProps {
  /** Single series of points: x label + y value. */
  data: Array<{ x: string | number; y: number }>;
  height?: number;
  formatValue?: (v: number) => string;
  className?: string;
  fill?: boolean;
}

/** Simple line chart cho time series. */
export function LineChart({
  data,
  height = 160,
  formatValue,
  className,
  fill = true,
}: LineChartProps): JSX.Element {
  if (data.length < 2) {
    return (
      <div className={cn("text-xs text-muted-foreground p-4", className)}>
        Cần ít nhất 2 điểm dữ liệu
      </div>
    );
  }
  const maxY = Math.max(1, ...data.map((d) => d.y));
  const minY = Math.min(0, ...data.map((d) => d.y));
  const range = maxY - minY || 1;
  const leftPad = 40;
  const rightPad = 16;
  const topPad = 16;
  const bottomPad = 28;
  const chartW = Math.max(360, data.length * 60);
  const chartH = height - topPad - bottomPad;
  const stepX = (chartW - leftPad - rightPad) / (data.length - 1);

  const points = data.map((d, i) => {
    const x = leftPad + i * stepX;
    const y = topPad + chartH - ((d.y - minY) / range) * chartH;
    return { x, y, raw: d };
  });

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const last = points[points.length - 1]!;
  const first = points[0]!;
  const areaPath =
    linePath +
    ` L ${last.x} ${topPad + chartH}` +
    ` L ${first.x} ${topPad + chartH} Z`;

  const yTicks = [0, 0.5, 1].map((p) => {
    const v = minY + range * (1 - p);
    const y = topPad + chartH * p;
    return { v, y };
  });

  return (
    <div className={cn("w-full overflow-x-auto", className)}>
      <svg width={chartW} height={height} className="text-foreground">
        {yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={leftPad}
              x2={chartW - rightPad}
              y1={t.y}
              y2={t.y}
              stroke="currentColor"
              className="text-muted-foreground/20"
              strokeWidth={1}
              strokeDasharray="2 2"
            />
            <text
              x={leftPad - 4}
              y={t.y + 3}
              textAnchor="end"
              className="text-[9px] fill-muted-foreground"
            >
              {formatValue ? formatValue(t.v) : Math.round(t.v)}
            </text>
          </g>
        ))}
        {fill ? (
          <path d={areaPath} fill="hsl(var(--primary) / 0.15)" stroke="none" />
        ) : null}
        <path
          d={linePath}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3} fill="hsl(var(--primary))">
            <title>
              {p.raw.x}: {formatValue ? formatValue(p.raw.y) : p.raw.y}
            </title>
          </circle>
        ))}
        {points.map((p, i) => (
          <text
            key={i}
            x={p.x}
            y={height - 8}
            textAnchor="middle"
            className="text-[9px] fill-muted-foreground"
          >
            {String(p.raw.x).slice(-5)}
          </text>
        ))}
      </svg>
    </div>
  );
}

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  className?: string;
}

/** Inline tiny line chart cho compact display. */
export function Sparkline({
  data,
  width = 100,
  height = 24,
  className,
}: SparklineProps): JSX.Element {
  if (data.length < 2) {
    return (
      <svg width={width} height={height} className={className}>
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="currentColor"
          className="text-muted-foreground/40"
          strokeWidth={1}
        />
      </svg>
    );
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);

  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  });

  return (
    <svg width={width} height={height} className={cn("text-primary", className)}>
      <polyline
        points={points.join(" ")}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface DonutSegment {
  label: string;
  value: number;
  color?: string;
}

interface DonutProps {
  data: DonutSegment[];
  size?: number;
  thickness?: number;
  className?: string;
}

const DEFAULT_COLORS = [
  "#8b5cf6", // violet
  "#3b82f6", // blue
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#a855f7", // purple
  "#ef4444", // red
];

/** Donut chart cho breakdown (vd by-provider cost). */
export function Donut({
  data,
  size = 120,
  thickness = 18,
  className,
}: DonutProps): JSX.Element {
  const total = data.reduce((sum, s) => sum + s.value, 0) || 1;
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  const segments = data.map((s, i) => {
    const portion = s.value / total;
    const length = portion * circumference;
    const dashArray = `${length} ${circumference - length}`;
    const dashOffset = -offset;
    offset += length;
    return {
      ...s,
      color: s.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length],
      dashArray,
      dashOffset,
      portion,
    };
  });

  return (
    <div className={cn("flex items-center gap-4", className)}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={thickness}
            className="text-muted/40"
          />
          {segments.map((s, i) => (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeDasharray={s.dashArray}
              strokeDashoffset={s.dashOffset}
            >
              <title>
                {s.label}: {(s.portion * 100).toFixed(1)}%
              </title>
            </circle>
          ))}
        </g>
      </svg>
      {/* Legend */}
      <ul className="text-xs space-y-1 flex-1 min-w-0">
        {segments.map((s, i) => (
          <li
            key={i}
            className="flex items-center gap-2 min-w-0"
            title={`${s.label}: ${s.value}`}
          >
            <span
              className="size-2.5 rounded-sm shrink-0"
              style={{ backgroundColor: s.color }}
            />
            <span className="truncate flex-1">{s.label}</span>
            <span className="text-muted-foreground">
              {(s.portion * 100).toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
